import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LegalFooter } from "./Legal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

export default function Login() {
  const { user, login, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLocked(false);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else {
      setError(res.error);
      setLocked(/bloccat|tentativi/i.test(res.error || ""));
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotSent("");
    const res = await forgotPassword(forgotEmail || email);
    setForgotLoading(false);
    if (res.ok) {
      setForgotSent(res.message);
      toast.success("Richiesta inviata");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <BrandLogo size="lg" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Bentornato</h1>
          <p className="text-sm text-slate-500 mt-1 mb-8">
            Accedi alla tua area professionista per gestire i preventivi.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            {error && !locked && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            {locked && (
              <div data-testid="login-lockout-alert" className="flex items-start gap-2.5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <ShieldAlert style={{ width: 18, height: 18 }} className="shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <p className="font-semibold">Accesso temporaneamente bloccato</p>
                  <p className="text-amber-700 leading-snug">{error}</p>
                </div>
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
            </Button>
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => { setForgotEmail(email); setForgotSent(""); setForgotOpen(true); }}
              className="w-full text-center text-sm font-medium text-slate-500 hover:text-primary transition-colors"
            >
              Password dimenticata?
            </button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Non hai un account? <button type="button" data-testid="login-to-register-link" onClick={() => navigate("/register")} className="text-primary font-medium hover:underline">Registrati gratis</button>
          </p>
          <LegalFooter className="mt-10" />
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Password dimenticata</DialogTitle>
            <DialogDescription>
              Inserisci la tua email: se è registrata riceverai un link per reimpostare la password (valido 1 ora).
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <p data-testid="forgot-password-sent" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              {forgotSent}
            </p>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3">
              <Input
                type="email"
                data-testid="forgot-password-email-input"
                placeholder="La tua email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="forgot-password-submit" disabled={forgotLoading}>
                  {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <div className="hidden lg:flex flex-1 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md text-white">
          <h2 className="text-3xl font-extrabold tracking-tight leading-tight">
            Preventivi interattivi che si vendono da soli.
          </h2>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Crea preventivi con add-on opzionali, condividi un link e lascia che il cliente
            componga la sua offerta vedendo il totale aggiornarsi in tempo reale.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {["Add-on dinamici", "Calcoli live", "Firma online"].map((t) => (
              <div key={t} className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <p className="text-sm font-semibold">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
